import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Contexte global de partage GPS : survit a la navigation entre les pages.
// Le partage ne s'arrete que sur action du chauffeur, deconnexion ou fermeture de l'onglet navigateur.
const GpsSharingContext = createContext(null);

export const GpsSharingProvider = ({ children }) => {
    const { token } = useAuth();
    const [sharing, setSharing] = useState(null); // vehicle id en cours de partage
    const [vehicleName, setVehicleName] = useState('');
    const [frequency, setFrequency] = useState(10); // secondes
    const [lastPosition, setLastPosition] = useState(null);
    const [sentCount, setSentCount] = useState(0);
    const [gpsError, setGpsError] = useState(null);
    const intervalRef = useRef(null);
    const wakeLockRef = useRef(null);
    const sharingRef = useRef(null);
    const tokenRef = useRef(token);
    tokenRef.current = token;

    const sendPosition = useCallback((vehicleId) => {
        if (!navigator.geolocation) {
            setGpsError("Geolocalisation non supportee par ce navigateur");
            return;
        }
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const { latitude, longitude, accuracy } = pos.coords;
                try {
                    await axios.put(`${API}/vehicles/${vehicleId}/update-location`, {
                        gps_latitude: latitude,
                        gps_longitude: longitude
                    }, { headers: { Authorization: `Bearer ${tokenRef.current}` } });
                    setLastPosition({ lat: latitude, lon: longitude, accuracy: Math.round(accuracy), time: new Date() });
                    setSentCount(c => c + 1);
                    setGpsError(null);
                } catch {
                    setGpsError("Echec d'envoi de la position (reseau ?)");
                }
            },
            (err) => {
                if (err.code === 1) setGpsError("Autorisation de localisation refusee. Activez-la dans les reglages du navigateur.");
                else if (err.code === 2) setGpsError("Position indisponible (GPS desactive ?)");
                else setGpsError("Delai GPS depasse, nouvelle tentative au prochain envoi");
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
        );
    }, []);

    const acquireWakeLock = async () => {
        try {
            if ('wakeLock' in navigator) {
                wakeLockRef.current = await navigator.wakeLock.request('screen');
            }
        } catch {
            // Wake Lock non supporte / refuse : non bloquant,
            // le partage continue mais l'ecran peut se mettre en veille.
            wakeLockRef.current = null;
        }
    };

    const startSharing = useCallback(async (vehicleId, freq, name = '') => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        const f = freq || 10;
        setFrequency(f);
        setSharing(vehicleId);
        setVehicleName(name);
        sharingRef.current = vehicleId;
        setSentCount(0);
        setGpsError(null);
        sendPosition(vehicleId);
        intervalRef.current = setInterval(() => sendPosition(vehicleId), f * 1000);
        await acquireWakeLock();
        toast.success('Partage de position demarre');
    }, [sendPosition]);

    const stopSharing = useCallback((silent = false) => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = null;
        setSharing(null);
        setVehicleName('');
        sharingRef.current = null;
        setLastPosition(null);
        if (wakeLockRef.current) { wakeLockRef.current.release(); wakeLockRef.current = null; }
        if (!silent) toast.info('Partage de position arrete');
    }, []);

    const changeFrequency = useCallback((f) => {
        setFrequency(f);
        if (sharingRef.current && intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = setInterval(() => sendPosition(sharingRef.current), f * 1000);
        }
    }, [sendPosition]);

    // Arret automatique a la deconnexion
    useEffect(() => {
        if (!token && sharingRef.current) stopSharing(true);
    }, [token, stopSharing]);

    // Re-acquerir le wake lock quand l'onglet navigateur redevient visible
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible' && sharingRef.current) acquireWakeLock();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, []);

    return (
        <GpsSharingContext.Provider value={{ sharing, vehicleName, frequency, lastPosition, sentCount, gpsError, startSharing, stopSharing, changeFrequency }}>
            {children}
        </GpsSharingContext.Provider>
    );
};

export const useGpsSharing = () => useContext(GpsSharingContext);
